import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import Toast from '../../../components/Common/Toast';
import { getPointOfSales } from '../../../services/pointOfSalesService';
import { ventasReportService } from '../../../services/ventasReportService';

const STORAGE_KEY = 'ventas_reports_product_margins_filters';

function defaultDates() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultDates(), pointOfSaleId: '', search: '', sortBy: 'revenue' };
    const parsed = JSON.parse(raw);
    const defaults = defaultDates();
    return {
      dateFrom: parsed.dateFrom || defaults.dateFrom,
      dateTo: parsed.dateTo || defaults.dateTo,
      pointOfSaleId: parsed.pointOfSaleId || '',
      search: parsed.search || '',
      sortBy: parsed.sortBy || 'revenue',
    };
  } catch {
    return { ...defaultDates(), pointOfSaleId: '', search: '', sortBy: 'revenue' };
  }
}

function saveStoredFilters(filters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

const formatMoney = (amount) =>
  `S/ ${Number(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPct = (value) =>
  value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`;

const formatQty = (value) =>
  Number(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });

const SORT_OPTIONS = [
  { value: 'revenue', label: 'Ventas' },
  { value: 'quantity', label: 'Cantidad' },
  { value: 'margin', label: 'Margen S/' },
  { value: 'margin_pct', label: 'Margen %' },
  { value: 'avg_unit_price', label: 'P. unitario' },
  { value: 'avg_unit_cost', label: 'C. unitario' },
  { value: 'unit_margin', label: 'Margen unitario' },
  { value: 'name', label: 'Nombre' },
];

export default function VentasProductMarginsReport() {
  const [filters, setFilters] = useState(loadStoredFilters);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getPointOfSales()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setPointsOfSale(res.data.filter((p) => p.is_active !== false));
        }
      })
      .catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      };
      if (filters.pointOfSaleId) {
        params.point_of_sale_id = filters.pointOfSaleId;
      }
      const res = await ventasReportService.getDashboard(params);
      if (res.success) {
        setRows(res.data?.all_products || []);
        setKpis(res.data?.kpis || null);
        setMeta(res.meta || null);
        setHasQueried(true);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el reporte de productos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom, filters.dateTo, filters.pointOfSaleId]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleRows = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    let list = rows;
    if (term) {
      list = list.filter(
        (row) =>
          String(row.name || '')
            .toLowerCase()
            .includes(term)
          || String(row.code || '')
            .toLowerCase()
            .includes(term),
      );
    }

    const key = filters.sortBy || 'revenue';
    return [...list].sort((a, b) => {
      if (key === 'name') {
        return String(a.name || '').localeCompare(String(b.name || ''), 'es');
      }
      const av = Number(a[key] ?? -Infinity);
      const bv = Number(b[key] ?? -Infinity);
      return bv - av;
    });
  }, [rows, filters.search, filters.sortBy]);

  const exportToExcel = async () => {
    if (!visibleRows.length) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Productos margen');

    sheet.mergeCells('A1:L1');
    sheet.getCell('A1').value = `Reporte de productos / márgenes — ${filters.dateFrom} a ${filters.dateTo}`;
    sheet.getCell('A1').font = { bold: true, size: 14 };
    sheet.addRow([]);

    const header = sheet.addRow([
      'N°',
      'Código',
      'Producto',
      'UM',
      'Cantidad',
      'P. unitario',
      'C. unitario',
      'Margen unitario',
      'Ventas',
      'Costo',
      'Margen S/',
      'Margen %',
    ]);
    header.font = { bold: true };

    visibleRows.forEach((row, idx) => {
      sheet.addRow([
        idx + 1,
        row.code || '',
        row.name || '',
        row.unit || '',
        row.quantity,
        row.avg_unit_price,
        row.avg_unit_cost,
        row.unit_margin,
        row.revenue,
        row.cost,
        row.margin,
        row.margin_pct,
      ]);
    });

    sheet.columns.forEach((col) => {
      col.width = 14;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte-productos-margen-${filters.dateFrom}_${filters.dateTo}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1500px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">
              Dashboard
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/orders" className="hover:text-teal-600">
              Ventas
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/reports" className="hover:text-teal-600">
              Reportes
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Productos / márgenes</span>
          </nav>

          <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Reporte de productos</h1>
              <p className="text-gray-600 mt-1 max-w-3xl">
                Todos los productos vendidos en el período con precio unitario promedio, costo unitario
                promedio (kardex) y margen. El costo depende de las salidas ligadas a cada venta.
              </p>
            </div>
            <button
              type="button"
              onClick={exportToExcel}
              disabled={!visibleRows.length}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              Exportar Excel
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Punto de venta</label>
                <select
                  value={filters.pointOfSaleId}
                  onChange={(e) => updateFilter('pointOfSaleId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos los POS</option>
                  {pointsOfSale.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.code} — {pos.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ordenar por</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => updateFilter('sortBy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={loadReport}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
                >
                  {loading ? 'Consultando…' : 'Consultar'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Buscar producto</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                placeholder="Nombre o código…"
                className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            {meta ? (
              <p className="text-xs text-gray-500 mt-3">
                Período {meta.date_from} → {meta.date_to}
                {meta.point_of_sale_id ? ' · filtrado por POS' : ' · todos los POS'}
              </p>
            ) : null}
          </div>

          {kpis ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Productos</div>
                <div className="text-2xl font-bold text-gray-800">{kpis.products_sold || 0}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Ventas netas</div>
                <div className="text-xl font-bold text-gray-800">{formatMoney(kpis.sales_net)}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Costo</div>
                <div className="text-xl font-bold text-amber-700">{formatMoney(kpis.cogs)}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Margen bruto</div>
                <div className="text-xl font-bold text-teal-700">
                  {formatMoney(kpis.margin)}
                  <span className="text-sm font-medium text-gray-500 ml-2">{formatPct(kpis.margin_pct)}</span>
                </div>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="text-center py-12 text-gray-500">Cargando reporte…</div>
          ) : !hasQueried ? null : visibleRows.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No hay productos vendidos para los filtros seleccionados</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cant.</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. unit.</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">C. unit.</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">M. unit.</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventas</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Costo</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margen</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margen %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {visibleRows.map((row, idx) => (
                      <tr key={row.product_id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-gray-400">{idx + 1}</td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-800">{row.name}</div>
                          {row.code ? <div className="text-xs font-mono text-gray-500">{row.code}</div> : null}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-600">{formatQty(row.quantity)}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{formatMoney(row.avg_unit_price)}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{formatMoney(row.avg_unit_cost)}</td>
                        <td className="px-3 py-3 text-right font-medium text-teal-700">
                          {formatMoney(row.unit_margin)}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-700">{formatMoney(row.revenue)}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{formatMoney(row.cost)}</td>
                        <td className="px-3 py-3 text-right font-semibold text-teal-700">
                          {formatMoney(row.margin)}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-700">{formatPct(row.margin_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-100">
                Mostrando {visibleRows.length} de {rows.length} producto(s)
              </div>
            </div>
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
}
